import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";

// #1055 — Gmail-style bulk scribe assignment. Given the union of judged
// positions across every SELECTED class (positions vary per class — FVD's
// judge panel decides which of E/B/C/H/M a class actually has), show one
// picker per position, defaulting to "leave unchanged" so an operator only
// touches the positions they mean to. On submit, the caller (ClassManager)
// loops the SAME per-class-per-position write used by the existing manual
// picker (ClassDetailDrawer) over every selected class, skipping any
// position that class doesn't judge.
export default function BulkAssignScribesDialog({
  open,
  onClose,
  classCount,
  positions,
  scribes,
  saving,
  error,
  onSubmit,
}) {
  const { t } = useTranslation("scoreadmin");
  // { [position]: "" (leave unchanged) | "__CLEAR__" | userId }
  const [assignments, setAssignments] = useState({});

  // This Dialog is already bumped above the AppBar (modal + 100, below); each
  // Select's own popover Menu defaults to zIndex.modal and would otherwise
  // render BEHIND the Dialog's container — same fix ClassDetailDrawer uses
  // for its pickers (menuProps at modal + 200, one layer above this Dialog).
  const menuProps = { sx: { zIndex: (theme) => theme.zIndex.modal + 200 } };

  // Reset picks each time the dialog opens (a fresh selection shouldn't carry
  // over a previous bulk-assign's in-progress choices).
  useEffect(() => {
    if (open) setAssignments({});
  }, [open]);

  const hasAnyChoice = Object.values(assignments).some((v) => v !== "");

  const handleSubmit = () => {
    onSubmit(assignments);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="bulk-assign-scribes-dialog"
      // AppLayout's AppBar (common-fe) is pinned at zIndex 1300, same as
      // JudgeSignatureModal — bump this dialog above it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle>
        <Typography variant="h6" color="text.primary">
          {t("ring.bulkAssign.title", { count: classCount })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("ring.bulkAssign.help")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || t("ring.loadError")}
          </Alert>
        )}

        {positions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("ring.bulkAssign.noPositions")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {positions.map((position) => (
              <FormControl key={position} size="small" fullWidth>
                <InputLabel id={`bulk-${position}-label`}>
                  {t("ring.positionScribe", { position })}
                </InputLabel>
                <Select
                  labelId={`bulk-${position}-label`}
                  label={t("ring.positionScribe", { position })}
                  value={assignments[position] ?? ""}
                  disabled={saving}
                  data-testid={`bulk-assign-select-${position}`}
                  MenuProps={menuProps}
                  onChange={(e) =>
                    setAssignments((prev) => ({ ...prev, [position]: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>{t("ring.bulkAssign.leaveUnchanged")}</em>
                  </MenuItem>
                  <MenuItem value="__CLEAR__">{t("ring.bulkAssign.clear")}</MenuItem>
                  {scribes.map((m) => (
                    <MenuItem key={m.userId} value={m.userId}>
                      {userDisplayName(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        {/* #1055 — MUI's default (unset) Button text color reads as
            near-invisible grey in dark mode (a repeat contrast bug in this
            codebase, e.g. JudgeSignatureModal's Cancel); force text.primary. */}
        <Button onClick={onClose} disabled={saving} sx={{ color: "text.primary" }}>
          {t("ring.bulkAssign.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !hasAnyChoice || positions.length === 0}
          data-testid="bulk-assign-scribes-submit"
        >
          {t("ring.bulkAssign.assign")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
